import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { PERMISSIONS } from '../lib/constants';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Search, Plus, Pencil, Trash2, Loader2, FileSpreadsheet, FileDown, CheckCircle2, XCircle, Pencil as PencilIcon } from 'lucide-react';
import { formatIDR } from '../lib/format';
import { toast } from 'sonner';
import { NumberInput } from '../components/NumberInput';
import { BulkActionBar, BulkButton } from '../components/BulkActionBar';
import { BulkEditDialog } from '../components/BulkEditDialog';

const CATS = ['Lighting', 'LED Screen', 'Rigging', 'Stage', 'Genset', 'Crew', 'Special FX', 'Multimedia', 'Other'];
const UNITS = ['Unit', 'Meter', 'Square Meter', 'Day', 'Event', 'Package', 'Person', 'Lot', 'Set'];

const empty = {
  product_name: '', category: 'Other', description: '', unit: 'Unit',
  default_price: 0, cost_price: 0, is_active: true, notes: '',
};

export default function Products() {
  const { role } = useAuth();
  const canEdit = PERMISSIONS.canEdit(role);
  const canDelete = PERMISSIONS.canDeleteFinancial(role);
  const canExport = PERMISSIONS.canImportExport(role);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [showActive, setShowActive] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  // Bulk selection state — Set of product IDs
  const [selected, setSelected] = useState(() => new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (category !== 'all') params.category = category;
      if (showActive !== 'all') params.is_active = showActive === 'active';
      const { data } = await api.get('/products', { params });
      setList(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search, category, showActive]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.product_name.trim()) { toast.error('Nama produk wajib'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        default_price: Number(form.default_price) || 0,
        cost_price: Number(form.cost_price) || 0,
      };
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success('Produk diperbarui');
      } else {
        await api.post('/products', payload);
        toast.success('Produk ditambahkan');
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Gagal'); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    try { await api.delete(`/products/${p.id}`); toast.success('Produk dihapus'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Gagal'); }
  };
  const toggleActive = async (p) => {
    try { await api.post(`/products/${p.id}/toggle-active`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Gagal'); }
  };

  const downloadFile = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: 'blob' });
      const link = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = link; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(link);
      toast.success('Berkas diunduh');
    } catch (e) { toast.error('Gagal mengunduh'); }
  };

  // ---- Bulk selection helpers ----
  const visibleIds = useMemo(() => list.map(p => p.id), [list]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
  const someVisibleSelected = visibleIds.some(id => selected.has(id));

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach(id => next.delete(id));
      else visibleIds.forEach(id => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action, payload = null) => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    setBulkBusy(true);
    try {
      const { data } = await api.post('/products/bulk', { action, ids, payload });
      const label = action === 'delete' ? 'dihapus' : action === 'set_active'
        ? (payload?.value ? 'diaktifkan' : 'dinonaktifkan')
        : 'diperbarui';
      toast.success(`${data.affected} produk ${label}`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal melakukan aksi massal');
    } finally { setBulkBusy(false); }
  };

  const exportSelected = () => {
    if (selected.size === 0) return;
    // Filter export by IDs by passing as query param (server supports id filter? if not, fall back to client-side)
    const idsParam = Array.from(selected).join(',');
    downloadFile(`/export/products?ids=${encodeURIComponent(idsParam)}`, 'produk-terpilih.xlsx');
  };

  return (
    <div className="space-y-4" data-testid="products-page">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="products-search-input" placeholder="Cari nama, deskripsi…" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kategori</SelectItem>
            {CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={showActive} onValueChange={setShowActive}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua</SelectItem>
            <SelectItem value="active">Aktif</SelectItem>
            <SelectItem value="inactive">Nonaktif</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        {canExport && <Button variant="secondary" onClick={() => downloadFile('/export/products', 'produk.xlsx')} data-testid="products-export"><FileSpreadsheet className="h-4 w-4 mr-2" />Export</Button>}
        {canExport && <Button variant="secondary" onClick={() => downloadFile('/import/templates/products', 'template-produk.xlsx')}><FileDown className="h-4 w-4 mr-2" />Template</Button>}
        {canEdit && <Button onClick={openNew} data-testid="products-add-button"><Plus className="h-4 w-4 mr-2" />Tambah Produk</Button>}
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          {/* Bulk Action Bar */}
          <div className="p-2">
            <BulkActionBar
              count={selected.size}
              onClear={clearSelection}
              itemLabel="produk"
              testid="products-bulk-bar"
            >
              {canEdit && (
                <BulkButton onClick={() => setBulkEditOpen(true)} data-testid="products-bulk-edit" disabled={bulkBusy}>
                  <PencilIcon className="h-4 w-4 mr-1.5" />Edit Massal
                </BulkButton>
              )}
              {canEdit && (
                <BulkButton onClick={() => runBulk('set_active', { value: true })} data-testid="products-bulk-activate" disabled={bulkBusy}>
                  <CheckCircle2 className="h-4 w-4 mr-1.5" />Aktifkan
                </BulkButton>
              )}
              {canEdit && (
                <BulkButton onClick={() => runBulk('set_active', { value: false })} data-testid="products-bulk-deactivate" disabled={bulkBusy}>
                  <XCircle className="h-4 w-4 mr-1.5" />Nonaktifkan
                </BulkButton>
              )}
              {canExport && (
                <BulkButton onClick={exportSelected} data-testid="products-bulk-export" disabled={bulkBusy}>
                  <FileSpreadsheet className="h-4 w-4 mr-1.5" />Export
                </BulkButton>
              )}
              {canDelete && (
                <BulkButton
                  onClick={() => setBulkDeleteOpen(true)}
                  className="text-red-300 hover:text-red-200"
                  data-testid="products-bulk-delete"
                  disabled={bulkBusy}
                >
                  <Trash2 className="h-4 w-4 mr-1.5" />Hapus
                </BulkButton>
              )}
            </BulkActionBar>
          </div>

          {/* Mobile card list (sm:hidden) */}
          <div className="md:hidden space-y-2 p-2 pt-0">
            {loading && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                <Loader2 className="inline h-4 w-4 animate-spin mr-2" />Memuat…
              </div>
            )}
            {!loading && list.length === 0 && (
              <div className="text-center py-10 text-muted-foreground text-sm">Belum ada produk.</div>
            )}
            {!loading && list.map(p => (
              <div
                key={p.id}
                className={`rounded-lg border p-3 transition-colors ${selected.has(p.id) ? 'border-primary/50 bg-primary/5' : 'border-border bg-card/60'}`}
                data-testid={`products-card-${p.id}`}
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={selected.has(p.id)}
                    onCheckedChange={() => toggleOne(p.id)}
                    aria-label={`Pilih ${p.product_name}`}
                    data-testid={`products-mobile-select-${p.id}`}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{p.product_name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                          <span className="px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10">{p.category}</span>
                          <span>•</span>
                          <span>{p.unit}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => canEdit && toggleActive(p)}
                        disabled={!canEdit}
                        className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full border ${p.is_active ? 'bg-emerald-400/15 text-emerald-300 border-emerald-400/30' : 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30'}`}
                      >
                        {p.is_active ? 'Aktif' : 'Nonaktif'}
                      </button>
                    </div>
                    {p.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</p>
                    )}
                    <div className="flex items-end justify-between mt-2 pt-2 border-t border-border">
                      <div>
                        <div className="text-[10px] text-muted-foreground">Harga Default</div>
                        <div className="font-mono-num font-semibold text-primary">{formatIDR(p.default_price)}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        {canEdit && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`products-mobile-edit-${p.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-300" data-testid={`products-mobile-delete-${p.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Hapus produk?</AlertDialogTitle>
                                <AlertDialogDescription>Penawaran/invoice yang sudah memakai produk tidak akan terpengaruh.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Batal</AlertDialogCancel>
                                <AlertDialogAction onClick={() => remove(p)}>Hapus</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden md:block overflow-x-auto">
            <Table data-testid="products-table">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="w-10 sticky left-0 bg-card z-10">
                    <Checkbox
                      checked={allVisibleSelected ? true : (someVisibleSelected ? 'indeterminate' : false)}
                      onCheckedChange={toggleAllVisible}
                      aria-label="Pilih semua produk terlihat"
                      data-testid="products-select-all"
                    />
                  </TableHead>
                  <TableHead>Nama Produk</TableHead>
                  <TableHead>Kategori</TableHead>
                  <TableHead>Satuan</TableHead>
                  <TableHead className="text-right">Harga Default</TableHead>
                  <TableHead className="text-right">Harga Pokok</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Memuat…</TableCell></TableRow>}
                {!loading && list.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Belum ada produk.</TableCell></TableRow>}
                {list.map(p => (
                  <TableRow
                    key={p.id}
                    className={`hover:bg-white/4 border-border ${selected.has(p.id) ? 'bg-primary/5' : ''}`}
                    data-testid={`products-row-${p.id}`}
                  >
                    <TableCell className="w-10 sticky left-0 bg-card z-10">
                      <Checkbox
                        checked={selected.has(p.id)}
                        onCheckedChange={() => toggleOne(p.id)}
                        aria-label={`Pilih ${p.product_name}`}
                        data-testid={`products-select-${p.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{p.product_name}</div>
                      {p.description && <div className="text-xs text-muted-foreground truncate max-w-[280px]">{p.description}</div>}
                    </TableCell>
                    <TableCell><span className="text-xs px-2 py-0.5 rounded-full bg-white/5 border border-white/10">{p.category}</span></TableCell>
                    <TableCell className="text-muted-foreground">{p.unit}</TableCell>
                    <TableCell className="text-right font-mono-num">{formatIDR(p.default_price)}</TableCell>
                    <TableCell className="text-right font-mono-num text-muted-foreground">{formatIDR(p.cost_price || 0)}</TableCell>
                    <TableCell>
                      <button onClick={() => canEdit && toggleActive(p)} disabled={!canEdit} className={`text-xs px-2 py-0.5 rounded-full border ${p.is_active ? 'bg-emerald-400/15 text-emerald-300 border-emerald-400/30' : 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30'}`} data-testid={`products-toggle-${p.id}`}>
                        {p.is_active ? 'Aktif' : 'Nonaktif'}
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)}><Pencil className="h-4 w-4" /></Button>}
                      {canDelete && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-red-300" data-testid={`products-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Hapus produk?</AlertDialogTitle><AlertDialogDescription>Penawaran/invoice yang sudah memakai produk tidak akan terpengaruh.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={() => remove(p)}>Hapus</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-card border-border">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Produk' : 'Produk Baru'}</DialogTitle>
            <DialogDescription>Lengkapi detail produk atau layanan.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs">Nama Produk *</Label>
              <Input data-testid="product-form-name" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="product-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Satuan</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger data-testid="product-form-unit"><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Harga Default (Rp)</Label>
              <NumberInput testid="product-form-price" className="h-11 md:h-9 text-base md:text-sm text-right font-mono-num" value={form.default_price} onChange={(v) => setForm({ ...form, default_price: v })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Harga Pokok / Cost (Rp)</Label>
              <NumberInput className="h-11 md:h-9 text-base md:text-sm text-right font-mono-num" value={form.cost_price} onChange={(v) => setForm({ ...form, cost_price: v })} />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs">Deskripsi</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs">Catatan</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="md:col-span-2 flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <Label className="text-sm cursor-pointer">Aktif (bisa dipilih saat membuat penawaran/invoice)</Label>
            </div>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} data-testid="product-form-save">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Simpan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <BulkEditDialog
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        count={selected.size}
        itemLabel="produk"
        fields={[
          { key: 'category', label: 'Kategori', type: 'select', options: CATS },
          { key: 'unit', label: 'Satuan', type: 'select', options: UNITS },
          { key: 'default_price', label: 'Harga Default (Rp)', type: 'number', placeholder: '0' },
          { key: 'cost_price', label: 'Harga Pokok / Cost (Rp)', type: 'number', placeholder: '0' },
          { key: 'is_active', label: 'Status Aktif', type: 'boolean' },
          { key: 'notes', label: 'Catatan', type: 'textarea', placeholder: 'Catatan internal…' },
        ]}
        onSubmit={(payload) => runBulk('update', payload)}
      />

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent data-testid="products-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus {selected.size} produk?</AlertDialogTitle>
            <AlertDialogDescription>
              Tindakan ini permanen. Penawaran/invoice yang sudah memakai produk tidak terpengaruh karena disimpan sebagai snapshot.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600"
              onClick={async () => { await runBulk('delete'); setBulkDeleteOpen(false); }}
              data-testid="products-bulk-delete-confirm"
            >
              Hapus Permanen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
